import api from "../services/api";

/**
 * Fetch navigation data for a specific time range
 * @param {string} baseRecordUrl - The base record URL (e.g., /user/records/{recordUid} or /consultation/expert/accepted/{consultationUid}/user/record/{recordUid})
 * @param {number} startMs - Start time in milliseconds
 * @param {number} endMs - End time in milliseconds
 * @returns {Promise<{data: number[], startMs: number, endMs: number, sampleRate: number, dataPoints: number}>}
 */
export const getNavigationData = async (baseRecordUrl, startMs, endMs) => {
  try {
    const response = await api.get(`${baseRecordUrl}/navigation/data`, {
      params: {
        startMs: startMs,
        endMs: endMs
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching navigation data:', error);
    throw error;
  }
};

/**
 * Check navigation data processing status
 * @param {string} baseRecordUrl - The base record URL
 * @returns {Promise<{status: string, progress?: number}>}
 */
export const getNavigationStatus = async (baseRecordUrl) => {
  try {
    const response = await api.get(`${baseRecordUrl}/navigation/status`);
    return response.data;
  } catch (error) {
    console.error('Error fetching navigation status:', error);
    throw error;
  }
};

// Processing is now triggered automatically by the backend when needed
// No explicit trigger endpoint is exposed

/**
 * Get navigation metadata
 * @param {string} baseRecordUrl - The base record URL
 * @returns {Promise<{dataPointsCount: number, status: string}>}
 */
export const getNavigationMetadata = async (baseRecordUrl) => {
  try {
    const response = await api.get(`${baseRecordUrl}/navigation/metadata`);
    return response.data;
  } catch (error) {
    console.error('Error fetching navigation metadata:', error);
    throw error;
  }
};

// Helper function to check if navigation data is available
export const isNavigationAvailable = async (baseRecordUrl) => {
  try {
    const status = await getNavigationStatus(baseRecordUrl);
    return status.status === 'Completed';
  } catch (error) {
    return false;
  }
};

// Helper function to wait for navigation processing with polling
export const waitForNavigationProcessing = async (baseRecordUrl, maxWaitMs = 30000, pollIntervalMs = 2000) => {
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitMs) {
    const status = await getNavigationStatus(baseRecordUrl);
    
    if (status.status === 'Completed') {
      return true;
    }
    
    if (status.status === 'Failed') {
      throw new Error('Navigation processing failed');
    }
    
    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
  }
  
  return false; // Timeout
};